import { SEOData, SEOTagsProps } from "./types";
import { Platform } from "react-native";
import { useEffect, useMemo } from "react";
import { useSEOConfig } from "@redux/slices/globalSlice";
import { Helmet } from "react-helmet-async";

const clearSEOFields = () => {
  const head = document.head;

  const metaTags = head.querySelectorAll("meta");
  metaTags.forEach((tag) => tag.remove());

  const canonicalLinks = head.querySelectorAll('link[rel="canonical"]');
  canonicalLinks.forEach((link) => link.remove());
};

const SEOTags = ({ routeName }: SEOData) => {
  const getSEOConfig = useSEOConfig();
  const isWeb = Platform.OS === "web";

  const seoData = useMemo(() => {
    if (!isWeb) return null;
    if (
      !getSEOConfig ||
      typeof getSEOConfig !== "object" ||
      Object.keys(getSEOConfig).length === 0
    ) {
      return null;
    }

    const data = getSEOConfig[routeName] as SEOTagsProps;

    if (!data || typeof data !== "object" || Object.keys(data).length === 0) {
      return null;
    }

    return data;
  }, [getSEOConfig, routeName, isWeb]);

  useEffect(() => {
    if (!isWeb || !seoData) return;
    clearSEOFields();
  }, [isWeb, seoData]);

  if (!isWeb || !seoData) return null;
  return (
    <Helmet prioritizeSeoTags>
      {seoData.link && <link rel="canonical" href={seoData.link} />}
      {Object.entries(seoData).map(([key, value]) => {
        if (key.startsWith("og")) {
          const kebabKey = key
            .replace(/^og/, "og:")
            .replace(/([A-Z])/g, (match) => `${match.toLowerCase()}`);
          return (
            <meta key={key} property={kebabKey} content={value as string} />
          );
        }
        if (["description", "keywords"].includes(key)) {
          return <meta key={key} name={key} content={value as string} />;
        }
        return null;
      })}
    </Helmet>
  );
};

export default SEOTags;
